import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config';
import type {
  GenerateMfaBackupCodesResult,
  MfaBackupCodeStatus,
  MfaRequirementResult,
  RecoverMfaWithBackupCodeParams,
  RecoverMfaWithBackupCodeResult,
  SetSecurityMfaRequiredParams,
} from '../../../../types/security';

interface CallableSuccess<T> {
  success: boolean;
  data: T;
}

/**
 * MFA のテナント強制設定を更新する（#2984）。
 * 同社 companyAdmin / 運営 admin 代行のみ。アドオン + Pro プラン以上が条件。
 */
export const setSecurityMfaRequired = async (
  params: SetSecurityMfaRequiredParams,
): Promise<void> => {
  const callable = httpsCallable<
    SetSecurityMfaRequiredParams,
    CallableSuccess<{ mfa_required: boolean }>
  >(functions, 'setSecurityMfaRequired');
  await callable(params);
};

/**
 * ログイン中ユーザー自身のテナントの MFA 強制状態を取得する（#2984）。
 * companyMember でも呼び出せる（強制ゲート用）。
 */
export const getMfaRequirement = async (): Promise<MfaRequirementResult> => {
  const callable = httpsCallable<Record<string, never>, CallableSuccess<MfaRequirementResult>>(
    functions,
    'getMfaRequirement',
  );
  const result = await callable({});
  return result.data.data;
};

/**
 * バックアップコードを発行（再発行）する（#2984）。
 * 平文コードはこのレスポンスの一度きりしか取得できない。
 */
export const generateMfaBackupCodes = async (): Promise<GenerateMfaBackupCodesResult> => {
  const callable = httpsCallable<
    Record<string, never>,
    CallableSuccess<GenerateMfaBackupCodesResult>
  >(functions, 'generateMfaBackupCodes');
  const result = await callable({});
  return result.data.data;
};

/**
 * バックアップコードの残数を取得する（#3123）。
 * 平文コードは返らない（マイページの残数表示・再発行導線用）。
 */
export const getMfaBackupCodeStatus = async (): Promise<MfaBackupCodeStatus> => {
  const callable = httpsCallable<Record<string, never>, CallableSuccess<MfaBackupCodeStatus>>(
    functions,
    'getMfaBackupCodeStatus',
  );
  const result = await callable({});
  return result.data.data;
};

/**
 * バックアップコードでログインする（#2984・#3123・未認証）。
 * 認証アプリの登録は維持したまま、このログインだけを通すカスタムトークンを受け取る。
 */
export const recoverMfaWithBackupCode = async (
  params: RecoverMfaWithBackupCodeParams,
): Promise<RecoverMfaWithBackupCodeResult> => {
  const callable = httpsCallable<
    RecoverMfaWithBackupCodeParams,
    CallableSuccess<RecoverMfaWithBackupCodeResult>
  >(functions, 'recoverMfaWithBackupCode');
  const result = await callable(params);
  return result.data.data;
};

/**
 * 認証アプリ登録の事前準備（#2984）。
 * GCIP の要件（メール検証済みであること）を満たすため、登録開始前に呼び出す。
 * 呼び出し後はトークンを強制リフレッシュすること。
 */
export const prepareMfaEnrollment = async (): Promise<void> => {
  const callable = httpsCallable<Record<string, never>, { success: boolean }>(
    functions,
    'prepareMfaEnrollment',
  );
  await callable({});
};
